import React from "react";
import { observer } from "mobx-react";
import {
  CalendarMonth,
  ImportContacts,
  FilePresent,
  Summarize,
  Medication,
  ShoppingCart,
} from "@mui/icons-material";
import { Grid, Tab, Tabs, CircularProgress, Box } from "@mui/material";
import EuroIcon from "@mui/icons-material/Euro";
import Badge from "@mui/material/Badge";
import { Link } from "react-router-dom";
import useStore from "../../helpers/useStore";
import { PatientsContext } from "../../stores/Patients/patients.provider";
import { useMasterTables } from "../../stores/MasterTables";
import { NoticeContext } from "../../stores/Notice/notice.provider";
import { TopLevelPaper } from "../../themes/StyledComponents";
import { mobiliTheme } from "../../themes/mobiliTheme";
import { usePatientRoute } from "../../routes/usePatientRoute";
import RxOverView from "../PatientOverview/RxInfo/RxOverView";
import ScheduleOverview from "../ScheduleOverview/ScheduleOverview";
import { DocumentListViewer } from "../Documents/DocumentListViewer";
import { PatientDatenschutzViewer } from "../Documents/PatientDatenschutzViewer";
import PatientNoticeCard from "../Notice/PatientNotice/PatientNoticeCard";
import Documentation from "../PatientDocumentation/documentation copy/Documentation";
import DoctorReportOverView from "../PatientDocumentation/doctorReport/DoctorReportForm/DoctorReportOverview";
import Sales from "../Sales/SalesPatientOverview/Sales";
import { PATIENT_SUBTABS } from "../../stores/Patients/patientsStore";
import { useUnpaidBadgeCount } from "../../stores/Transactions/transaction.queries";
import PatientBilling from "../pages/PatientBills/components/PatientBilling";

interface PatientRightPanelProps {}

const PatientRightPanel = observer(({}: PatientRightPanelProps) => {
  const PatientsStore = useStore(PatientsContext);
  // React Query: master tables data
  const NoticeStore = useStore(NoticeContext);

  const {
    selectedSubTabStr: selectedTab,
    setSelectedSubTabStr,
    selectedPatientId,
  } = PatientsStore;

  const { data: badges } = useUnpaidBadgeCount(selectedPatientId || 0);

  const renderTabContent = () => {
    switch (selectedTab) {
      case PATIENT_SUBTABS.PRESCRIPTIONS:
        return (
          <RxOverView
            openPatientTab={() => {}}
            setPatientData={() => {}}
            handleData={() => {}}
          />
        );
      case PATIENT_SUBTABS.APPOINTMENTS:
        return <ScheduleOverview />;
      case PATIENT_SUBTABS.BILLING:
        // return <PatientRechnungen />;
        return <PatientBilling />;
      case "documents":
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <DocumentListViewer />
            </Grid>
            <Grid item xs={12} md={4}>
              <PatientNoticeCard />
            </Grid>
            <Grid item xs={12} md={3}>
              <PatientDatenschutzViewer />
            </Grid>
          </Grid>
        );
      case "documentation":
        return <Documentation/>;
      case "doctor-reports":
        return <DoctorReportOverView routingParams={{ pId: selectedPatientId }} />;
      case "sales":
        return <Sales />;
      default:
        return <div>Select a tab to view patient details</div>;
    }
  };

  return (
    <>
      <Tabs
        orientation="horizontal"
        value={selectedTab}
        onChange={(_, newValue) => setSelectedSubTabStr(newValue)}
        aria-label="sub-tabs"
        sx={{ minHeight: "", paddingLeft: mobiliTheme.topLevelPadding }}
      >
        <Tab
          iconPosition="start"
          label="Rezepte"
          icon={<ImportContacts />}
          value={PATIENT_SUBTABS.PRESCRIPTIONS}
        />
        <Tab
          iconPosition="start"
          label="Termine"
          icon={
            <CalendarMonth
              sx={{
                fontWeight:
                  selectedTab === PATIENT_SUBTABS.APPOINTMENTS
                    ? "bold"
                    : "normal",
              }}
            />
          }
          value={PATIENT_SUBTABS.APPOINTMENTS}
          sx={{
            marginRight: "4px",
          }}
        />
        <Tab
          iconPosition="start"
          label={
            <span style={{ position: "relative" }}>
              {badges && badges.dueBillsCount > 0 ? (
                <Badge
                  badgeContent={badges.dueBillsCount}
                  color="error"
                  sx={{
                    "& .MuiBadge-badge": {
                      transform: "translate(16px, -12px)",
                    },
                  }}
                >
                  Rechnungen
                </Badge>
              ) : (
                "Rechnungen"
              )}
            </span>
          }
          icon={
            <EuroIcon
              sx={{
                fontWeight:
                  selectedTab === PATIENT_SUBTABS.BILLING ? "bold" : "normal",
              }}
            />
          }
          value={PATIENT_SUBTABS.BILLING}
        />
        <Tab
          iconPosition="start"
          label={
            <span style={{ position: "relative" }}>
              {NoticeStore.patientNoticeDataArr.length ? (
                <Badge
                  badgeContent={NoticeStore.patientNoticeDataArr.length}
                  color="info"
                  sx={{
                    "& .MuiBadge-badge": {
                      transform: "translate(16px, -12px)",
                    },
                  }}
                >
                  e-Akte
                </Badge>
              ) : (
                "e-Akte"
              )}
            </span>
          }
          icon={
            <FilePresent
              sx={{
                fontWeight: selectedTab === "documents" ? "bold" : "normal",
              }}
            />
          }
          value="documents"
        />
        <Tab
          iconPosition="start"
          label="Dokumentation"
          icon={
            <Summarize
              sx={{
                fontWeight: selectedTab === "documentation" ? "bold" : "normal",
              }}
            />
          }
          value="documentation"
        />
        <Tab
          iconPosition="start"
          label="Therapieberichte"
          icon={
            <Medication
              sx={{
                fontWeight:
                  selectedTab === "doctor-reports" ? "bold" : "normal",
              }}
            />
          }
          value="doctor-reports"
        />
        <Tab
          iconPosition="start"
          label="Verkauf"
          icon={
            <ShoppingCart
              sx={{
                fontWeight: selectedTab === "sales" ? "bold" : "normal",
              }}
            />
          }
          value="sales"
        />
      </Tabs>

      <TopLevelPaper sx={{ borderTopRightRadius: "0px", height: "100%" }}>
        {renderTabContent()}
      </TopLevelPaper>
    </>
  );
});

export default PatientRightPanel;
